import {makeCheckedInput} from 'common/utils/form-util';
import {serializeEntries} from 'common/utils/url-util';

import TableAppCache from 'lib/table-app-cache';

/*
 * Controller for cached table app checkboxes.
 * Interfaces between the TableAppCache instance
 * and DOM to keep model and view synced.
 */
class Checkboxes {
  /*
   * @param actionsSelector: selector of the actions dropdown, for hiding.
   *   Exclude to always keep visible.
   * @param allSelector: id of "all" checkboxes indicator
   * @param appId: identifies which table app rows are being cached.
   * @param counterSelector: selector of the number of rows selected display
   * @param inputName: name attribute on checkbox inputs
   * @param onUpdate: callback that fires on start and on checkbox change
   * @param orgId: identifies which org's rows are being cached.
   * @param rowCount: total number of rows across all pages
   */
  constructor({
    actionsSelector,
    allSelector,
    appId,
    counterSelector,
    inputName,
    onUpdate = () => {},
    orgId,
    rowCount,
  }) {
    this._cache = new TableAppCache({
      appId,
      orgId,
      rowCount,
    });
    this._inputName = inputName;
    this._onUpdate = onUpdate;

    this._actionsDropdownNode = actionsSelector &&
      document.querySelector(actionsSelector)

    this._allCheckboxesNode = document.querySelector(allSelector);
    this._checkboxNodes = Array.from(
      document.querySelectorAll(`input[name=${this._inputName}]`));
    this._counterNode = document.querySelector(counterSelector);

    this._updateRowChecksFromState();
    this._updateAllCheckboxUI();
    this._updateCounter();
    this._updateActionsDropdownUI();
    setTimeout(this._onUpdate);

    this._addClickHandlers();
  }

  /*
   * Add click handlers to row checkboxes as well
   * as "all" checkbox"
   */
  _addClickHandlers() {
    if (this._allCheckboxesNode) {
      this._allCheckboxesNode.addEventListener('click', () => this.onClickAll());
    }
    this._checkboxNodes.forEach(node => {
      node.addEventListener('change', (e) => this.onClickRow(e.target.value));
    });
  }

  /*
   * Initiate row checkmarks on page load, reading
   * rows from cache.
   */
  _updateRowChecksFromState() {
    const isAllMode = this._cache.isAllMode();
    const clickedRows = new Set(this._cache.getClickedRowIds());
    this._checkboxNodes.forEach(node => {
      const rowIsClicked = clickedRows.has(node.value);
      node.checked = isAllMode ? !rowIsClicked : rowIsClicked;
    });
  }

  /*
   * Handle when user clicks the "all" checkbox.
   * If no rows are selected, select all; otherwise, deselect all.
   */
  onClickAll() {
    this._cache.onClickAll();
    this._updateAllCheckboxUI();
    this._updateAllRowsUI();
    this._updateActionsDropdownUI();
    this._updateCounter();
    this._onUpdate();
  }

  onClickRow(rowId) {
    this._cache.onClickRow(rowId);
    this._updateAllCheckboxUI();
    this._updateActionsDropdownUI();
    this._updateCounter();
    this._onUpdate();
  }

  _updateActionsDropdownUI() {
    if (!this._actionsDropdownNode) {
      return
    }

    this._actionsDropdownNode.style.display =
      this._cache.noRowsSelected() ? 'none' : 'initial';
  }

  /*
   * Updates the "all" checkbox UI to indicate
   * whether all, none, or some rows are selected.
   */
  _updateAllCheckboxUI() {
    if (!this._allCheckboxesNode) {
      return;
    }

    const all  = 'col-check-all';
    const some = 'col-check-some';
    const none = 'col-check-none';

    this._allCheckboxesNode.classList.remove(all, some, none);

    let indicatorClass;
    if (this._cache.allRowsSelected()) {
      indicatorClass = all;
    } else if (this._cache.noRowsSelected()) {
      indicatorClass = none;
    } else {
      indicatorClass = some;
    }

    this._allCheckboxesNode.classList.add(indicatorClass);
  }

  _updateAllRowsUI() {
    const checked = this._cache.isAllMode();
    this._checkboxNodes.forEach(node => node.checked = checked);
  }

  _updateCounter() {
    const numSelected = this._cache.getNumSelected();
    if (this._counterNode) {
      this._counterNode.innerText = numSelected;
    }
  }

  /*
   * Returns an array of tuples of [name, id]
   * for the selected checkboxes and exclude key
   */
  getResultParams() {
    const result = this._cache.getClickedRowIds()
      .map(rowId => ([this._inputName, rowId]));
    if (this._cache.isAllMode()) {
      result.push(['exclude', '1']);
    }
    return result;
  }

  /*
   * @param name: use specified name for input `name` attr
   */
  getFormHtml(name, extra = []) {
    const result = this.getResultParams()
      .map(([_name, value]) => {
        let key = _name;
        if (name && _name !== 'exclude') {
          key = name;
        }
        return makeCheckedInput(key, value);
      });
    extra.forEach(([key, value]) =>
      result.push(makeCheckedInput(key, value)));
    return result;
  }

  serialize() {
    return '&' + serializeEntries(this.getResultParams());
  }

  clearSelections() {
    this._cache.clearSelections();
    this._updateAllCheckboxUI();
    this._updateAllRowsUI();
    this._updateActionsDropdownUI();
    this._updateCounter();
    this._onUpdate();
  }

  getNumSelected() {
    return this._cache.getNumSelected();
  }
};

export default Checkboxes;

